/**
 * @voltxampere/presets — Parallel resistor pair (Sprint 50-2)
 *
 * Two resistors in parallel — 1/R_total = 1/R1 + 1/R2. The
 * complement of `simple-resistor-pair`. With V1=10V and R1=R2=1kΩ,
 * the parallel resistance is 500Ω and the total current is
 * I_total = 10/500 = 20 mA, split evenly into 10 mA per branch.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { makeCircuit, makeNet, makeComponent } from "../_builders.js";
export const parallelResistors = {
    id: "parallel-resistors",
    name: { tr: "Paralel İki Direnç", en: "Two Resistors in Parallel" },
    category: "basic",
    difficulty: 1,
    description: {
        tr: "İki direnci paralel bağlayın. Toplam = (R1·R2)/(R1+R2). V1=10V, R1=R2=1kΩ → I_top=20mA.",
        en: "Two resistors in parallel. Total = (R1·R2)/(R1+R2). V1=10V, R1=R2=1kΩ → I_total=20mA.",
    },
    circuit: makeCircuit({
        slot: 1,
        name: "Parallel Resistors",
        description: "Two resistors in parallel",
        nets: [makeNet({ name: "gnd", isGround: true }), makeNet({ name: "vcc" })],
        components: [
            makeComponent({
                id: "V1",
                type: "voltage-source",
                params: { voltage: 10 },
                terminals: { pos: "net_vcc", neg: "net_gnd" },
                label: "V1",
                position: { x: -160, y: 0 },
                rotation: 0,
            }),
            makeComponent({
                id: "R1",
                type: "resistor",
                params: { resistance: 1000 },
                terminals: { p1: "net_vcc", p2: "net_gnd" },
                label: "R1",
                position: { x: 0, y: 0 },
                rotation: 90,
            }),
            makeComponent({
                id: "R2",
                type: "resistor",
                params: { resistance: 1000 },
                terminals: { p1: "net_vcc", p2: "net_gnd" },
                label: "R2",
                position: { x: 80, y: 0 },
                rotation: 90,
            }),
            makeComponent({
                id: "GND1",
                type: "ground",
                params: {},
                terminals: { p: "net_gnd" },
                position: { x: -160, y: 80 },
                rotation: 0,
            }),
        ],
        wires: [
            {
                id: "w_vcc",
                netId: "net_vcc",
                path: [
                    { x: -160, y: -10 },
                    { x: -160, y: -60 },
                    { x: 80, y: -60 },
                    { x: 80, y: -20 },
                ],
                style: "orthogonal",
            },
            {
                id: "w_gnd",
                netId: "net_gnd",
                path: [
                    { x: -160, y: 10 },
                    { x: -160, y: 71 },
                    { x: 80, y: 71 },
                    { x: 80, y: 20 },
                ],
                style: "orthogonal",
            },
        ],
    }),
    expectedValues: [
        {
            node: "vcc",
            analysis: "dc",
            value: 10,
            tolerance: 0.001,
            unit: "V",
        },
    ],
    learningGoals: [
        {
            tr: "Paralelde toplam direnç tek koldan küçüktür.",
            en: "Parallel total is smaller than any single branch.",
        },
        {
            tr: "Her kol aynı gerilimi görür (KVL).",
            en: "Each branch sees the same voltage (KVL).",
        },
    ],
    references: ["Sedra/Smith, Microelectronic Circuits, Ch. 1"],
};
//# sourceMappingURL=parallel-resistors.js.map